import { useEffect, useId, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Radio, X } from 'lucide-react'
import { Field, IconButton, Input } from '@/components/common'
import { nativeSportsbookApi } from '@/services/api'
import { resolveSampleCollection } from './collectionModel'
import { normalizeRuntimePreviewCatalog } from './nativeRuntimeCatalog'
import { PREVIEW_CATALOG, bettableMarkets, clockDisplay, formatStartTime } from './nativePreviewModel'
import { eventSearchDefinition, referencedEventQueryKey } from './nativeEventReferences'
import styles from './nativeEventSearch.module.css'
import { isLiveSource } from './catalogSources'

const MIN_QUERY = 2

// The search always asks the same source the preview renders from: the tenant
// runtime under live data, the sample inventory otherwise. Rehearsal never narrows it.
function useEventSearch({ slug, source, regulation, query, state }) {
  const text = query.trim()
  return useQuery({
    queryKey: ['native-event-search', slug, source, regulation, state, text.toLowerCase()],
    enabled: text.length >= MIN_QUERY && (source === 'sample' || Boolean(slug)),
    queryFn: async ({ signal }) => {
      const definition = eventSearchDefinition(text, state)
      if (source === 'sample') return resolveSampleCollection(definition, PREVIEW_CATALOG).events
      const result = await nativeSportsbookApi.previewCollection(slug, definition, signal, null, regulation, source)
      if (result.state === 'unavailable') throw new Error('Games are unavailable right now. Retry shortly.')
      return normalizeRuntimePreviewCatalog({ ...result, freshness: 'replicated' }).events
    },
    staleTime: 15000, retry: false,
  })
}

function eventTiming(event) {
  if (event.live) return event.clock ? `Live · ${clockDisplay(event.clock)}` : 'Live'
  return event.start_time ? formatStartTime(event.start_time) : ''
}

function EventSummary({ event }) {
  const meta = [event.league, eventTiming(event), Array.isArray(event.markets) && !bettableMarkets(event).length ? 'No open markets' : ''].filter(Boolean)
  return <>
    <span className={styles.name}>{event.name}</span>
    <span className={styles.meta}>{meta.join(' · ')}</span>
  </>
}

// A field that names one game. Shows the resolved game, or the saved id with a
// warning when the current inventory no longer has it, and searches the
// catalog to change it. `state` is 'live' for fields that require an in-play game.
export function NativeEventSearch({ label, labelExtra, help, value = '', event = null, state = 'active', disabled = false, catalog, slug, onSelect, onClear, placeholder = 'Search teams, matchups or competitions', missingMessage = 'Not in the current games. The app skips this until you choose another.' }) {
  const generatedId = useId()
  const listId = `${generatedId}-results`
  const inputRef = useRef(null)
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(!value)
  const [activeIndex, setActiveIndex] = useState(-1)
  const source = isLiveSource(catalog.source) ? catalog.source : 'sample'
  const regulation = catalog.regulation ?? null
  const search = useEventSearch({ slug, source, regulation, query, state })
  const results = search.data || []
  const open = searching || !value
  useEffect(() => { setActiveIndex(-1) }, [query, results.length])
  useEffect(() => {
    if (activeIndex < 0) return
    document.getElementById(`${generatedId}-option-${activeIndex}`)?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex, generatedId])

  const choose = (next) => {
    // Seed the referenced-event cache so the preview and dependent controls
    // render the pick before the workspace refetches it by id.
    queryClient.setQueryData(referencedEventQueryKey(slug, source, regulation, next.id), next)
    onSelect(next)
    setQuery('')
    setSearching(false)
  }
  const startSearch = () => { setSearching(true); requestAnimationFrame(() => inputRef.current?.focus()) }
  const onKeyDown = (keyEvent) => {
    if (keyEvent.key === 'Escape') {
      if (activeIndex >= 0) setActiveIndex(-1)
      else if (query) setQuery('')
      else if (value) setSearching(false)
      return
    }
    if (!results.length) return
    if (keyEvent.key === 'ArrowDown') { keyEvent.preventDefault(); setActiveIndex((index) => (index < results.length - 1 ? index + 1 : 0)) }
    else if (keyEvent.key === 'ArrowUp') { keyEvent.preventDefault(); setActiveIndex((index) => (index > 0 ? index - 1 : results.length - 1)) }
    else if (keyEvent.key === 'Enter' && activeIndex >= 0) { keyEvent.preventDefault(); choose(results[activeIndex]) }
  }
  const stale = value && event && state === 'live' && !event.live
  const missing = value && !event
  const status = search.isFetching ? 'Searching…'
    : search.isError ? search.error.message
      : query.trim().length >= MIN_QUERY && search.isSuccess ? `${results.length} game${results.length === 1 ? '' : 's'}${state === 'live' ? ' live' : ''}`
        : query.trim().length ? `Type at least ${MIN_QUERY} characters` : ''

  if (!open) {
    return (
      <Field label={label} labelExtra={labelExtra} help={help} error={missing ? missingMessage : stale ? 'This game is no longer live.' : undefined}>
        <div className={styles.selected} data-missing={missing ? 'true' : 'false'}>
          <button type="button" className={styles.selectedButton} disabled={disabled} aria-label={`Change ${label}`} onClick={startSearch}>
            {event ? <EventSummary event={event} /> : <><span className={styles.name}>Event {value}</span><span className={styles.meta}>Not in the current games</span></>}
          </button>
          {onClear ? <IconButton variant="ghost" size="small" label={`Clear ${label}`} disabled={disabled} onClick={() => { onClear(); setSearching(true) }}><X size={13} /></IconButton> : null}
        </div>
      </Field>
    )
  }
  return (
    <div className={styles.search}>
      <Field label={label} labelExtra={labelExtra} help={help}>
        <Input
          ref={inputRef}
          size="compact"
          type="search"
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={results.length > 0}
          aria-haspopup="listbox"
          aria-controls={results.length > 0 ? listId : undefined}
          aria-activedescendant={activeIndex >= 0 ? `${generatedId}-option-${activeIndex}` : undefined}
          onChange={(changed) => setQuery(changed.target.value)}
          onKeyDown={onKeyDown}
        />
      </Field>
      <div className={styles.status} role="status" aria-live="polite">{status}{value && !search.isFetching ? <button type="button" className={styles.cancel} onClick={() => { setQuery(''); setSearching(false) }}>Keep current</button> : null}</div>
      {search.isSuccess && query.trim().length >= MIN_QUERY && results.length === 0 ? <p className={styles.empty}>{state === 'live' ? 'No live game matches. Try a team playing now.' : 'No game matches. Try a team or competition name.'}</p> : null}
      {results.length > 0 ? (
        <ul id={listId} role="listbox" className={styles.results} aria-label={`${label} results`}>
          {results.map((candidate, index) => (
            <li key={candidate.id} role="presentation">
              <button
                id={`${generatedId}-option-${index}`}
                type="button"
                role="option"
                aria-selected={candidate.id === value}
                className={styles.result}
                data-active={activeIndex === index ? 'true' : 'false'}
                data-live={candidate.live ? 'true' : 'false'}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choose(candidate)}
              >
                {candidate.live ? <Radio size={12} className={styles.liveDot} aria-hidden="true" /> : null}
                <EventSummary event={candidate} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
